// import api's and mutations
import { mutations, queryKeys } from 'RestAPIs/rapini';

// import components
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { LoadingOverlay, showSnackbar } from 'components/shared/index';
import Actions from '../../Actions';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';

// import hooks
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// import types
import { DutyCodeForm, EditDutyCodeProps } from '../EditDutyCode.types';

// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import { omit } from 'lodash';
import dayjs from 'dayjs';
import editDutyCodeSchema from '../utils/editDutyCodeSchema.util';

function EditDutyCode({
  isOpen,
  dutyCode,
  handleClose,
  onSave,
  organizationalUnitId
}: EditDutyCodeProps) {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    resolver: yupResolver(editDutyCodeSchema()),
    defaultValues: {
      displayDutyCode: dutyCode?.code,
      colorCode: dutyCode?.colorCode,
      validFrom: dayjs(dutyCode?.validFrom).toDate() || dayjs().toDate(),
      validTo: dayjs(dutyCode?.validTo).toDate() || dayjs().toDate()
    }
  });

  const { control, handleSubmit } = methods;

  const { mutate: updateDutyCode, isLoading: isUpdating } =
    mutations.useUpdateDutyCode();

  const QueryClient = useQueryClient();

  function onSubmit(data: DutyCodeForm) {
    updateDutyCode(
      {
        ...omit(dutyCode, ['dutyCode', 'colorCode', 'validTo']),
        code: data.displayDutyCode,
        colorCode: data.colorCode,
        validFrom: data.validFrom.toISOString(),
        validTo: data.validTo.toISOString(),
        organizationalUnitId: organizationalUnitId,
        dutyCodeId: dutyCode.dutyCodeId
      },
      {
        onSuccess: () => {
          QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
          onSave();
        },
        onError: (error: any) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      }
    );
  }

  function onDialogClose() {
    handleClose();
  }

  return (
    <StyledDialogMedium
      open={isOpen}
      title={dutyCode ? t('editDutyCode') : t('addDutyCode')}
      handleClose={onDialogClose}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DutyCodeBaseFields />
          <ControlledValidFromValidTo
            control={control}
            validFromFieldName="validFrom"
            validToFieldName="validTo"
          />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isUpdating} />
        </form>
      </FormProvider>
    </StyledDialogMedium>
  );
}

export default EditDutyCode;
